import { useChat } from '@ai-sdk/react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { FC, useEffect, useRef, useState } from 'react';

import { aiAssistantSampleQuestions } from '../../../domain/ragChat/sampleQuestions';
import { aiAssistantColors } from '../../../domain/ragChat/theme';
import {
  type RagChatCustomData,
  isRagChatCustomData,
} from '../../../domain/ragChat/types';

import { useAuthFetch } from '../../../hooks/useAuthFetch';

import { captureSentryException } from '../../../utils/captureSentryError';
import { gaClickEvent } from '../../../utils/googleAnalytics';
import { sortBy } from '../../../utils/sortBy';
import { Tooltip } from '../../atoms/Tooltip/Tooltip';
import { InputForm } from './InputForm';
import { MessageList } from './MessageList';

// --- Error Message ---
const ErrorMessage = ({ error }: { error: Error }) => {
  return (
    <Alert severity="error" sx={{ mt: 2 }}>
      エラーが発生しました。:{error.message || 'Unknown error'}
    </Alert>
  );
};

// --- AiAssistantContent ---
const AiAssistantContent: FC<{ onReset: () => void; onClose: () => void }> = ({
  onReset,
  onClose,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const authFetch = useAuthFetch();

  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    status,
    error,
    data,
  } = useChat({
    api: `${process.env.REACT_APP_API_HOST}/rag/chat`,
    // memo: useAuthFetchを使用してアクセストークンを付与。認証エラー時はログイン画面へリダイレクト
    fetch: authFetch,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    streamProtocol: 'data',
    onFinish: (m) => {
      // memo:ストリームが終了したときの処理
      // 今後セッションID毎に会話を保持する場合はここでreduxなどに保存する
      // memo: ストリームが終了したとき、フォーカスを戻す
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    },
    onError: (error) => {
      console.error('Error:', error);
      // Sentryにエラーログを送信
      captureSentryException(error, 'ai_assistant_error');
    },
  });

  /**
   * カスタムデータの型を保証する
   */
  const customData = data?.map((item) => {
    if (isRagChatCustomData(item)) {
      return item; // 型が保証されている
    }
    // 型が保証されていない場合の処理
    const itemData = item as RagChatCustomData;
    return {
      chatHistoryId: itemData?.chatHistoryId || '',
      sessionId: itemData?.sessionId || '',
      reference_documents: itemData?.reference_documents || [],
      timestamp: itemData?.timestamp || '',
      prompt: itemData?.prompt || '',
    } as RagChatCustomData;
  });

  // メッセージ追従
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, status]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    handleSubmit(e, {
      body: { question: input },
    });

    // 送信ボタンもしくはEnterキーで送信時
    gaClickEvent('AIアシスタント質問送信');
  };

  const handleReset = () => {
    onReset();
  };

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const sampleQuestions = sortBy(
    // 表示フラグがtrueの物のみを表示させる
    aiAssistantSampleQuestions.filter((question) => question.isView),
    (question) => question.order
  );

  return (
    <Box
      // ガイド用のクラス名を追加
      className="joyride-ai-input"
      sx={{
        width: '100%',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          p: 1,
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            ml: 1,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: '1rem' }} />
          <Box sx={{ ml: 1 }}>AIアシスタント</Box>
        </Box>
        <Box ml="auto" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            aria-label="ai-assistant-menu"
            onClick={handleMenuOpen}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={!!menuAnchorEl}
            onClose={handleMenuClose}
            sx={
              { zIndex: 200000 } //ChatPlusのzIndexが100001なので、それよりも大きなものにしておく。数値変動があるかは不明だが、余裕を持たせておく
            }
          >
            <MenuItem
              onClick={() => {
                handleMenuClose();
                handleReset();
                gaClickEvent('AIアシスタントチャット履歴消去');
              }}
            >
              <ListItemIcon>
                <DeleteForeverIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>チャット履歴消去</ListItemText>
            </MenuItem>
          </Menu>
          <Tooltip title="閉じる" arrow>
            <IconButton
              aria-label="close-ai-assistant"
              onClick={() => {
                onClose();
                gaClickEvent('AIアシスタント閉じる');
              }}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* メインコンテンツエリア */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          py: 0,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {messages.length ? (
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 1,
              bgcolor: 'background.default',
            }}
          >
            <MessageList
              messages={messages}
              status={status}
              data={customData}
            />
            <Box ref={messagesEndRef} />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 1,
              bgcolor: 'background.default',
            }}
          >
            <Box>
              <Box>
                <Typography
                  variant="h5"
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    mt: 2,
                    background: aiAssistantColors.background, // グラデーションの設定
                    WebkitBackgroundClip: 'text', // テキストのみにグラデーションを適用
                    WebkitTextFillColor: 'transparent', // テキストの塗りつぶしを透明に
                  }}
                >
                  AIに質問してみましょう。
                </Typography>
              </Box>
              <Box mt={2} sx={{ overflow: 'hidden' }}>
                {sampleQuestions.map((question, index) => (
                  <Grow
                    key={index}
                    in={true}
                    timeout={1500}
                    mountOnEnter
                    style={{
                      transitionDelay: `${index * 80}ms`,
                      transformOrigin: '0 100% 0',
                    }}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{
                        mb: 1,
                        fontWeight: 'bold',
                      }}
                      onClick={() => {
                        handleInputChange({
                          target: { value: question.value },
                        } as React.ChangeEvent<HTMLInputElement>);
                        inputRef.current?.focus();
                        gaClickEvent('AIアシスタントサンプル質問選択', {
                          id: `aiSampleQuestionId-${question.id}`,
                        });
                      }}
                    >
                      {question.value}
                    </Button>
                  </Grow>
                ))}
              </Box>
            </Box>

            <Box ref={messagesEndRef} />
          </Box>
        )}

        {error && <ErrorMessage error={error} />}

        {/* フッター部分：メッセージ入力欄 */}
        <Box sx={{ mt: 1 }}>
          <InputForm
            input={input}
            handleInputChange={handleInputChange}
            handleFormSubmit={handleFormSubmit}
            isLoading={status === 'streaming'}
            inputRef={inputRef}
          />
        </Box>
        {/* 免責文 */}
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              lineHeight: 1.2,
              display: 'inline-block',
            }}
          >
            AIアシスタントは、人工知能が自動生成した情報を提供しております。これらの情報の正確性や信頼性については保証いたしかねますので、内容をご確認のうえ参考としてご活用ください。
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export const AiAssistant: FC<{ onClose: () => void }> = ({ onClose }) => {
  // sessionKeyを更新することでuseChatフックが再マウントされ、セッションidが変更されます。
  // 会話の履歴が破棄され、会話が初期化します。
  const [sessionKey, setSessionKey] = useState(0);
  const handleReset = () => setSessionKey((prev) => prev + 1);

  return (
    <AiAssistantContent
      key={sessionKey}
      onReset={handleReset}
      onClose={onClose}
    />
  );
};
